import React, { useState } from 'react';
import { Info, Zap, Fuel, TrendingUp } from 'lucide-react';

export default function CostsandSavings() {
  const [miles, setMiles] = useState<number | string>(1200);
  const [gasPrice, setGasPrice] = useState<number | string>(3.50);
  const [mpg, setMpg] = useState<number | string>(25);
  const [elecRate, setElecRate] = useState<number | string>(0.03);
  const [evEfficiency, setEvEfficiency] = useState<number | string>(3.5);

  // Helper to safely parse numbers for math
  const safeNum = (val: number | string) => Number(val) || 0;

  // Math Logic
  const gasCost = (safeNum(miles) / safeNum(mpg)) * safeNum(gasPrice);
  const evCost = (safeNum(miles) / safeNum(evEfficiency)) * safeNum(elecRate);
  const monthlySavings = gasCost - evCost;
  const annualSavings = monthlySavings * 12;

  // Bar Chart Logic
  const maxCost = Math.max(gasCost, evCost, 1); 
  const gasBarWidth = `${(gasCost / maxCost) * 100}%`;
  const evBarWidth = `${(evCost / maxCost) * 100}%`;

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 md:p-8 max-w-3xl mx-auto my-8 font-sans">
      <div className="mb-8 border-b border-slate-100 pb-4">
        <h2 className="text-2xl font-black text-slate-900 flex items-center gap-2">
          <TrendingUp className="w-6 h-6 text-blue-600" />
          Gas vs. EV Savings Calculator
        </h2>
        <p className="text-slate-500 mt-2 font-medium">
          See exactly how much money your EV is saving you every month.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Controls Section */}
        <div className="space-y-6">
          
          {/* Miles Driven */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <label className="text-sm font-bold text-slate-700">Miles Driven per Month</label>
              <div className="flex items-center gap-1 bg-slate-50 border border-slate-200 rounded-md px-2 py-1 focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500">
                <input type="number" min="100" max="10000" value={miles} onChange={(e) => setMiles(e.target.value)} className="w-14 text-sm font-black text-blue-600 bg-transparent outline-none text-right appearance-none" />
                <span className="text-slate-400 text-xs font-bold">mi</span>
              </div>
            </div>
            <input type="range" min="100" max="3000" step="50" value={safeNum(miles)} onChange={(e) => setMiles(Number(e.target.value))} className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-blue-600" />
          </div>

          {/* Local Gas Price */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <label className="text-sm font-bold text-slate-700">Local Gas Price</label>
              <div className="flex items-center gap-1 bg-slate-50 border border-slate-200 rounded-md px-2 py-1 focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500">
                <span className="text-slate-400 text-xs font-bold">$</span>
                <input type="number" min="1.00" max="10.00" step="0.01" value={gasPrice} onChange={(e) => setGasPrice(e.target.value)} className="w-12 text-sm font-black text-blue-600 bg-transparent outline-none text-right appearance-none" />
                <span className="text-slate-400 text-xs font-bold">/gal</span>
              </div>
            </div>
            <input type="range" min="1.50" max="6.00" step="0.05" value={safeNum(gasPrice)} onChange={(e) => setGasPrice(Number(e.target.value))} className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-blue-600" />
          </div>

          {/* Comparable Gas MPG */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <label className="text-sm font-bold text-slate-700">Comparable Gas MPG</label>
              <div className="flex items-center gap-1 bg-slate-50 border border-slate-200 rounded-md px-2 py-1 focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500">
                <input type="number" min="5" max="100" value={mpg} onChange={(e) => setMpg(e.target.value)} className="w-12 text-sm font-black text-blue-600 bg-transparent outline-none text-right appearance-none" />
                <span className="text-slate-400 text-xs font-bold">MPG</span>
              </div>
            </div>
            <input type="range" min="10" max="60" step="1" value={safeNum(mpg)} onChange={(e) => setMpg(Number(e.target.value))} className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-blue-600" />
          </div>

          {/* Home Electricity Rate */}
          <div className="p-4 bg-slate-50 rounded-xl border border-slate-100">
            <div className="flex justify-between items-center mb-2 group relative">
              <div className="flex items-center gap-1.5 cursor-help">
                <label className="text-sm font-bold text-slate-700">Electricity Rate</label>
                <Info className="w-4 h-4 text-slate-400" />
                <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block w-64 bg-slate-900 text-white text-xs p-3 rounded-lg shadow-xl z-10">
                  Found on your monthly home utility bill. Look for the "Energy Charge" or combine the "Supply" and "Delivery" rates.
                </div>
              </div>
              <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-md px-2 py-1 focus-within:border-slate-500 focus-within:ring-1 focus-within:ring-slate-500">
                <span className="text-slate-400 text-xs font-bold">$</span>
                <input type="number" min="0.01" max="1.00" step="0.01" value={elecRate} onChange={(e) => setElecRate(e.target.value)} className="w-12 text-sm font-black text-slate-900 bg-transparent outline-none text-right appearance-none" />
                <span className="text-slate-400 text-xs font-bold">/kWh</span>
              </div>
            </div>
            <input type="range" min="0.01" max="0.40" step="0.01" value={safeNum(elecRate)} onChange={(e) => setElecRate(Number(e.target.value))} className="w-full h-2 bg-slate-300 rounded-lg appearance-none cursor-pointer accent-slate-900" />
          </div>

          {/* EV Efficiency */}
          <div className="p-4 bg-slate-50 rounded-xl border border-slate-100">
            <div className="flex justify-between items-center mb-2 group relative">
              <div className="flex items-center gap-1.5 cursor-help">
                <label className="text-sm font-bold text-slate-700">EV Efficiency</label>
                <Info className="w-4 h-4 text-slate-400" />
                <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block w-72 bg-slate-900 text-white text-xs p-3 rounded-lg shadow-xl z-10">
                  Tesla dashboards display efficiency in Watt-hours per mile (Wh/mi). To get mi/kWh, divide 1000 by your car's number. <br/><br/><strong>Example:</strong> 1000 ÷ 250 Wh/mi = 4.0 mi/kWh.
                </div>
              </div>
              <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-md px-2 py-1 focus-within:border-slate-500 focus-within:ring-1 focus-within:ring-slate-500">
                <input type="number" min="1.0" max="10.0" step="0.1" value={evEfficiency} onChange={(e) => setEvEfficiency(e.target.value)} className="w-12 text-sm font-black text-slate-900 bg-transparent outline-none text-right appearance-none" />
                <span className="text-slate-400 text-xs font-bold">mi/kWh</span>
              </div>
            </div>
            <input type="range" min="2.0" max="5.0" step="0.1" value={safeNum(evEfficiency)} onChange={(e) => setEvEfficiency(Number(e.target.value))} className="w-full h-2 bg-slate-300 rounded-lg appearance-none cursor-pointer accent-slate-900" />
          </div>

        </div>

        {/* Results Section */}
        <div className="flex flex-col">
          <div className="bg-slate-900 rounded-2xl p-6 text-white shadow-lg mb-6 flex-1 flex flex-col justify-center relative overflow-hidden">
            <div className="absolute top-0 right-0 p-4 opacity-10">
              <Zap className="w-32 h-32" />
            </div>
            <p className="text-slate-400 font-bold uppercase tracking-widest text-xs mb-1 relative z-10">You Are Saving</p>
            <div className="relative z-10">
              <span className="text-5xl font-black text-blue-400">
                ${monthlySavings > 0 ? monthlySavings.toFixed(0) : "0"}
              </span>
              <span className="text-lg text-slate-300 font-medium ml-2">/ month</span>
            </div>
            
            <div className="mt-6 pt-6 border-t border-slate-700/50 relative z-10">
              <p className="text-slate-400 font-bold uppercase tracking-widest text-xs mb-1">Annual Savings</p>
              <div className="text-3xl font-bold text-white">
                ${annualSavings > 0 ? annualSavings.toFixed(0) : "0"}
              </div>
            </div>
          </div>

          {/* Visual Bar Comparison */}
          <div className="space-y-4 pt-2">
            <div>
              <div className="flex justify-between text-xs font-bold text-slate-500 mb-1">
                <span className="flex items-center gap-1"><Fuel className="w-3 h-3" /> Gas Cost</span>
                <span>${gasCost.toFixed(0)}/mo</span>
              </div>
              <div className="w-full bg-slate-100 rounded-full h-3">
                <div className="bg-slate-400 h-3 rounded-full transition-all duration-500" style={{ width: gasBarWidth }}></div>
              </div>
            </div>
            
            <div>
              <div className="flex justify-between text-xs font-bold text-blue-600 mb-1">
                <span className="flex items-center gap-1"><Zap className="w-3 h-3" /> EV Cost</span>
                <span>${evCost.toFixed(0)}/mo</span>
              </div>
              <div className="w-full bg-blue-50 rounded-full h-3">
                <div className="bg-blue-500 h-3 rounded-full transition-all duration-500" style={{ width: evBarWidth }}></div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Required style to hide number input arrows in modern browsers */}
      <style>{`
        input[type=number]::-webkit-inner-spin-button, 
        input[type=number]::-webkit-outer-spin-button { 
          -webkit-appearance: none; 
          margin: 0; 
        }
        input[type=number] {
          -moz-appearance: textfield;
        }
      `}</style>
    </div>
  );
}